import { useCallback, useEffect, useMemo, useState } from "react";
import { trackEvent } from "@/lib/telemetry";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ChevronDown, ChevronRight, Download, EyeOff, FileText, GitCompare, GitMerge, Link2, Plus, RotateCcw, Search, X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SCRAPERS } from "@/lib/constants";
import { isLedgerDemoUser, ledgerFetch, setLedgerDemoMode, getDemoReportPortfolio, getDemoReportFundNames } from "@/lib/ledgerDemoSource";
import { buildHtmlReport } from "@/lib/demoReport";
import DocumentDetailDialog from "@/components/documents/DocumentDetailDialog";
import GenerateReportDialog from "@/components/dashboard/GenerateReportDialog";
import DocumentTimelineTable from "@/components/dashboard/DocumentTimelineTable";
import LinesTable from "@/components/dashboard/LinesTable";
import ExportLinesDialog from "@/components/dashboard/ExportLinesDialog";
import LedgerFilterBar from "@/components/ledger/LedgerFilterBar";
import PortalCard from "@/components/ledger/PortalCard";
import CompareSheet from "@/components/ledger/CompareSheet";
import ValueSparkline from "@/components/ledger/ValueSparkline";
import ConfidenceChip from "@/components/ledger/ConfidenceChip";
import PositionTimeline from "@/components/ledger/PositionTimeline";
import DiscardControl from "@/components/ledger/DiscardControl";
import EditableMetricCell, { OVERRIDABLE_FIELDS } from "@/components/ledger/EditableMetricCell";
import { V4EditContext, useV4Edit, V4LinesTable, V4PointsList, V4AddRow, V4RemoveLeaf, V4RenameFund, docBlobName } from "@/components/ledger/V4Edits";
import CalcHover, { fetchPoints } from "@/components/ledger/CalcHover";
import { explain } from "@/lib/ledgerCalc";
import MergeFundsDialog from "@/components/ledger/MergeFundsDialog";
import LinkMemberDialog from "@/components/ledger/LinkMemberDialog";
import { findActiveOverlay, revertOverlay, revertOverlaysForStreams, NOTE_MAX_LEN } from "@/components/ledger/overlayApi";
import { collectMergedStreamIds, collectLinkedStreamIds } from "@/components/ledger/overlayGroups";
import { applyLedgerFilters, applyLinesFilters, sortLines } from "@/lib/ledgerFilters";
import {
  buildFundDisplayMap, buildMemberDisplayMap, buildGroupResolver, buildLinesFacets, buildFuzzyCanon, commitmentRows,
} from "@/lib/linesDisplay";
import { sortFunds, FUND_SORT_METRICS } from "@/lib/ledgerLeaderboard";
import { buildBookTree } from "@/lib/bookTree";

// The client's plan is the only gate: Full + data -> ledger, Full + no data -> preparing card,
// not Full -> lock screen (also shown if the backend answers 403 "ledger_locked").
function LedgerLockedState() {
  const navigate = useNavigate();
  return (
    <Card className="max-w-xl mx-auto mt-12">
      <CardContent className="py-10 text-center space-y-3">
        <h1 className="text-xl font-semibold text-gray-900">The Ledger is part of Full Elementar</h1>
        <p className="text-sm text-gray-500">Upgrade to see all your funds and positions in one place.</p>
        <Button onClick={() => navigate("/settings?tab=billing&upgrade=1")}>Upgrade</Button>
      </CardContent>
    </Card>
  );
}

function LedgerPreparingState() {
  return (
    <Card className="max-w-xl mx-auto mt-12">
      <CardContent className="py-10 text-center space-y-2">
        <h1 className="text-xl font-semibold text-gray-900">Your ledger is being prepared.</h1>
        <p className="text-sm text-gray-500">It will appear here once your first documents are processed.</p>
      </CardContent>
    </Card>
  );
}

// ── formatting (mirrors DashboardPage / DemoPortfolioTree) ───────────────────────
const PORTAL_DISPLAY_NAMES = Object.fromEntries(SCRAPERS.map((s) => [s.id, s.name]));
PORTAL_DISPLAY_NAMES._unattributed = "Email";
const portalLabel = (id) => PORTAL_DISPLAY_NAMES[id] || id;

// Display-only: "alpine-ridge-ventures-ii-lp" -> "Alpine Ridge Ventures II LP".
const ROMAN = new Set(["i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x"]);
const UPPER_WORDS = new Set(["lp", "llc", "scsp", "sicav", "gp"]);
export function prettyFund(name) {
  if (typeof name !== "string" || !/^[a-z0-9]+(-[a-z0-9]+)+$/.test(name)) return name;
  return name
    .split("-")
    .map((w) => (ROMAN.has(w) || UPPER_WORDS.has(w) ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)))
    .join(" ");
}

function safeCurrencyCode(currency) {
  const n = String(currency || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(n) ? n : "USD";
}
function fmtMoney(amount, currency = "USD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: safeCurrencyCode(currency), minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
}
const fmtRatio = (v) => (v == null ? "—" : Number(v).toFixed(2));
const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
// irr is a decimal fraction from the API (e.g. 0.2188 = 21.88%); irr_reliable=false means no dated cashflow.
const fmtIrr = (irr, reliable) => {
  if (!reliable || irr == null) return null; // caller renders "N/A"
  return `${(Number(irr) * 100).toFixed(1)}%`;
};
// NAV/Inv = nav / capital_contributed_cumulative (RVPI).
// "—" when invested <= 0 or nav null. Rendered as a multiple e.g. "1.02×".
function fmtNavInv(nav, invested) {
  if (nav == null || invested == null || Number(invested) <= 0) return "—";
  return `${(Number(nav) / Number(invested)).toFixed(2)}×`;
}

// ── Invested-centric helpers ─────────────────────────────────────────────────────
// Returns true only when a member has a real undrawn pledge (commitment > contributed).
// Never coerces null to 0 — absence of data is not "zero unfunded".
const hasRealUnfunded = (m) =>
  m.commitment_total != null &&
  m.capital_contributed_cumulative != null &&
  Number(m.commitment_total) > Number(m.capital_contributed_cumulative);

const fundHasUnfunded = (members) => (members || []).some(hasRealUnfunded);

// ── Kit-driven column system ─────────────────────────────────────────────────────

// Predicates: given a member row, return true if the column should show a value for that member.
const SHOW = {
  always: () => true,
  has_contributed: (m) => m.capital_contributed_cumulative != null && Number(m.capital_contributed_cumulative) > 0,
  has_distributions: (m) => m.distributions_cumulative != null && Number(m.distributions_cumulative) > 0,
  has_real_unfunded: (m) => hasRealUnfunded(m),
  irr_reliable: (m) => m.irr_reliable === true && m.irr != null,
};

// Default PE-shaped kit — used when fund.kit is absent so the page renders identically to today.
const DEFAULT_KIT = {
  class: "pe",
  label: "Private Equity",
  columns: [
    { id: "commitment_total",              label: "Commitment",    kind: "money",    show: "has_real_unfunded" },
    { id: "uncalled",                      label: "Uncalled",      kind: "money",    show: "has_real_unfunded" },
    { id: "capital_contributed_cumulative", label: "Invested",      kind: "money",    show: "always" },
    { id: "distributions_cumulative",      label: "Distributed",   kind: "money",    show: "always" },
    { id: "nav",                           label: "Current Value", kind: "money",    show: "always",  as_of: "nav_as_of" },
    { id: "nav_inv",                       label: "Value/Inv",     kind: "nav_inv",  show: "always" },
    { id: "tvpi",                          label: "Net multiple",  kind: "ratio",    show: "always" },
    { id: "irr",                           label: "Return %",      kind: "pct",      show: "always" },
  ],
};

// Compute the list of columns that are actually visible for a given fund+members combo.
// A column with show==="always" always appears.
// A conditional column appears iff at least one member satisfies its predicate.
function getVisibleColumns(fund, members) {
  const kit = fund.kit || DEFAULT_KIT;
  const cols = kit.columns || DEFAULT_KIT.columns;
  // Inject nav_inv column after nav if not already present (for custom kits that predate this feature)
  const hasNavInv = cols.some((c) => c.id === "nav_inv");
  const enrichedCols = hasNavInv ? cols : cols.reduce((acc, col) => {
    acc.push(col);
    if (col.id === "nav") {
      acc.push({ id: "nav_inv", label: "Value/Inv", kind: "nav_inv", show: "always" });
    }
    return acc;
  }, []);

  return enrichedCols.filter((col) => {
    const pred = SHOW[col.show] || (() => true);
    if (col.show === "always") return true;
    return (members || []).some(pred);
  });
}

// Build the header label for a column, appending "(as of)" when as_of is set.
function colHeaderLabel(col) {
  if (col.as_of) return `${col.label} (as of)`;
  return col.label;
}

// One labelled KPI cell for the cashflow header strip.
// calc = {metric, points, shown, currency} -> hover shows how the figure was reached (v4 only: needs the rows).
function Kpi({ label, value, extra, calc = null }) {
  const body = <span className="font-mono text-gray-900">{value}</span>;
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
        {label} {extra}
      </span>
      {calc ? (
        <CalcHover align="left" compute={() => explain(calc.metric, calc.points, calc.shown, (n) => fmtMoney(n, calc.currency))}>{body}</CalcHover>
      ) : body}
    </div>
  );
}

// Hover over a calculated figure of a v4 row/fund: rows are fetched on first hover (timeline points), then explained.
// sources = [{leafId, fundName, investorName, positionKey?, label?}]; null when the row is not a v4 leaf.
function CalcCell({ metric, sources, shown, currency, version, children, align = "right" }) {
  if (!sources || !sources.length) return children;
  return (
    <CalcHover
      align={align}
      cacheKey={version}
      load={() => fetchPoints(sources)}
      compute={(points) => explain(metric, points, shown, (n) => fmtMoney(n, currency))}
    >
      {children}
    </CalcHover>
  );
}

const COL_METRIC = {
  capital_contributed_cumulative: "called", capital_called_cumulative: "called", distributions_cumulative: "distributed",
  nav: "value", nav_inv: "rvpi", dpi: "dpi", rvpi: "rvpi", tvpi: "tvpi", irr: "xirr",
};

// ── #39 fix: merge a POST /poc-inspection/input response's `projection` into the displayed
// kpis object — the SAME liveProjection pattern validated on /ledger-inspection
// (LedgerInspectionPage.jsx's mergeProjection), ported to /ledger's cashflow-KPI shape.
// Never invents a value: absent proj -> return priorKpis unchanged.
function mergeKpisFromProjection(proj, priorKpis) {
  if (!proj) return priorKpis;
  return {
    ...(priorKpis || {}),
    commitment: proj.commitment,
    called: proj.called,
    distributed: proj.distributed,
    nav: proj.nav,
    dpi: proj.dpi,
    tvpi: proj.tvpi,
    xirr: proj.xirr,
    xirr_confidence: proj.xirr_confidence,
    nav_status: proj.nav_status,
    currency: proj.currency || priorKpis?.currency,
  };
}

// Valid add-line types for the POC input plane.
// action mapping: add_line → capital_call_event_amount / distribution_event_amount
//                 supply_nav → nav
//                 assert_close → (no type)
// NEVER include xirr/tvpi/dpi/rvpi — the backend 400s them.
// "Commitment" is deliberately absent: the backend allowlist rejects commitment_total with a 400.
const POC_ADD_TYPES = [
  { label: "Capital Call", value: "capital_call_event_amount", action: "add_line" },
  { label: "Distribution", value: "distribution_event_amount", action: "add_line" },
  { label: "NAV", value: "nav", action: "supply_nav" },
  { label: "Mark Closed", value: "_mark_closed", action: "assert_close" },
];

// v4: the tenant's latest navs.csv / cashflows.csv / diff.csv from GET /api/ledger/v4-csv (auth header, so fetched then saved as a blob)
async function saveV4Csv(params, name) {
  const r = await ledgerFetch(`/api/ledger/v4-csv?${new URLSearchParams(params)}`,
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
  if (!r.ok) { toast.error(`Download failed (HTTP ${r.status})`); return; }
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Member's document timeline (fetched on expand) → cumulative + ↑/↓ deltas + PDF popup ──
// "Ledger | Timeline": one of the two at a time, remembered per browser (storage may be blocked — then it is just per visit).
const VIEW_KEY = "ledger.positionView";
const readView = () => { try { return localStorage.getItem(VIEW_KEY) === "timeline" ? "timeline" : "ledger"; } catch { return "ledger"; } };
const saveView = (v) => { try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore */ } };

function MemberTimeline({ fundName, investorName, currency, streamId, positionKey = null, editable = false, leafId = null }) {
  const [docs, setDocs] = useState(null);
  const [points, setPoints] = useState(null); // v4 only: the timeline view's points; null = no timeline view
  const [view, setViewState] = useState(readView);
  const setView = (v) => { setViewState(v); saveView(v); };
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Add-line form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addDate, setAddDate] = useState("");
  const [addTypeSel, setAddTypeSel] = useState(POC_ADD_TYPES[0].value);
  const [addAmount, setAddAmount] = useState("");
  const [addCcy, setAddCcy] = useState(currency || "USD");
  const [addNote, setAddNote] = useState("");

  const v4 = useV4Edit();
  const token = localStorage.getItem("token");
  const qs = new URLSearchParams({ fund_name: fundName, investor_name: investorName });
  // positionKey="" is a valid value (member-level residual) — check != null, not truthiness
  if (positionKey != null) qs.set("position_key", positionKey);
  if (leafId) qs.set("leaf", leafId); // v4: the leaf id picks the member

  const fetchTimeline = useCallback(() => {
    ledgerFetch(`/api/ledger/timeline?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => setDocs(j.documents || []))
      .catch(() => setDocs([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundName, investorName, token, positionKey]);

  const fetchCashflow = useCallback(() => {
    ledgerFetch(`/api/ledger/cashflow?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => setKpis(j.kpis || null))
      .catch(() => setKpis(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundName, investorName, token, positionKey]);

  const refetchAll = useCallback(() => {
    fetchTimeline();
    fetchCashflow();
  }, [fetchTimeline, fetchCashflow]);

  useEffect(() => {
    let cancelled = false;
    const _token = localStorage.getItem("token");
    const _qs = new URLSearchParams({ fund_name: fundName, investor_name: investorName });
    if (positionKey != null) _qs.set("position_key", positionKey);
    if (leafId) _qs.set("leaf", leafId);
    setLoading(true);
    Promise.all([
      ledgerFetch(`/api/ledger/timeline?${_qs}`, { headers: { Authorization: `Bearer ${_token}` } })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((j) => { if (!cancelled) { setDocs(j.documents || []); setPoints(j.points || null); } })
        .catch(() => { if (!cancelled) setDocs([]); }),
      ledgerFetch(`/api/ledger/cashflow?${_qs}`, { headers: { Authorization: `Bearer ${_token}` } })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((j) => { if (!cancelled) setKpis(j.kpis || null); })
        .catch(() => { if (!cancelled) setKpis(null); }),
    ]).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fundName, investorName, positionKey, leafId, v4.version]);

  // #39 fix: POST to the POC input endpoint, then CONSUME the response's `projection` and merge
  // it into the displayed kpis immediately (the /ledger-inspection liveProjection pattern) —
  // never claim "recomputed" unless the response actually contained a projection. Refetch stays
  // scoped to the evidence list (fetchTimeline) — the dumb bridge's /cashflow read is not the
  // source of the numbers shown right after this action.
  const handleAddLine = async (e) => {
    e.preventDefault();
    if (!editable) {
      toast.info("Read-only in the ledger view.");
      return;
    }
    if (!streamId) {
      toast.info("Adding entries is part of the full product — coming soon.");
      return;
    }
    const typeMeta = POC_ADD_TYPES.find((t) => t.value === addTypeSel) || POC_ADD_TYPES[0];
    const isClose = typeMeta.action === "assert_close";
    if (!addDate || (!isClose && !addAmount)) return;
    setAddBusy(true);
    try {
      const body = {
        stream_id: streamId,
        action: typeMeta.action,
        event_date: addDate,
        ...(isClose ? {} : {
          type: addTypeSel,
          amount: parseFloat(addAmount),
          currency: addCcy || undefined,
        }),
        ...(addNote.trim() ? { note: addNote.trim().slice(0, NOTE_MAX_LEN) } : {}),
      };
      const res = await ledgerFetch("/api/ledger/poc-inspection/input", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? ": " + txt.slice(0, 120) : ""}`);
      }
      const json = await res.json().catch(() => null);
      const proj = json?.projection;
      if (proj) {
        setKpis((prev) => mergeKpisFromProjection(proj, prev));
        toast.success("Entry added — metrics recomputed");
      } else {
        toast.success("Entry added");
      }
      setShowAddForm(false);
      setAddDate(""); setAddAmount(""); setAddNote("");
      fetchTimeline(); // evidence list only — kpis already reflect the merged projection above
    } catch (err) {
      toast.error("Add failed: " + (err?.message || "error"));
    } finally {
      setAddBusy(false);
    }
  };

  // Discard a client-input row (source=client_input) by its numeric input_id.
  // Extracted rows have non-numeric ledger_ids and are never discardable here.
  const handleReject = async (contribution) => {
    if (!editable || !streamId || !contribution?.ledger_id) {
      toast.info("Read-only in the ledger view.");
      return;
    }
    // Extracted rows: show tooltip; do not POST
    if (!contribution.source || contribution.source === "extracted") {
      toast.info("Extracted fields are view-only — correct the source document.");
      return;
    }
    try {
      const res = await ledgerFetch(`/api/ledger/poc-inspection/input/${contribution.ledger_id}/discard`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json().catch(() => null);
      const proj = json?.projection;
      if (proj) {
        setKpis((prev) => mergeKpisFromProjection(proj, prev));
        toast.success("Entry removed — metrics recomputed");
      } else {
        toast.success("Entry removed");
      }
      fetchTimeline();
    } catch (err) {
      toast.error("Remove failed: " + (err?.message || "error"));
    }
  };

  if (loading) return <div className="px-4 py-3"><Skeleton className="h-20 w-full rounded" /></div>;

  // Sort docs newest-first for the table display.
  // The sparkline internally re-sorts ascending by event_date for the x-axis.
  const docsNewestFirst = (docs || []).slice().sort((a, b) => {
    const ad = a.event_date || "";
    const bd = b.event_date || "";
    return bd < ad ? -1 : bd > ad ? 1 : 0;
  });

  const isClose = POC_ADD_TYPES.find((t) => t.value === addTypeSel)?.action === "assert_close";
  // v4 only: the timeline points are the rows behind every KPI above
  const calcOf = (metric, shown) => (v4.isV4 && points ? { metric, points, shown, currency } : null);

  return (
    <div className="px-4 pb-3">
      {/* Cashflow KPI header — commitment is a reference figure at the top, NOT a ledger movement.
          XIRR/TVPI/DPI are computed over the signed dated cashflow series (pg_cashflow_service). */}
      {kpis && (
        <div className="mb-3 rounded-md border border-gray-200 bg-white">
          <div className="flex items-baseline justify-between px-3 py-2 border-b border-gray-100">
            <span className="text-[10px] uppercase tracking-wider text-gray-400">Commitment</span>
            <span className="font-mono text-sm font-semibold text-gray-900">{fmtMoney(kpis.commitment, currency)}</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-x-4 gap-y-2 px-3 py-2 text-xs">
            <Kpi label="Called" value={fmtMoney(kpis.called, currency)} calc={calcOf("called", kpis.called)} />
            <Kpi label="Distributed" value={fmtMoney(kpis.distributed, currency)} calc={calcOf("distributed", kpis.distributed)} />
            <Kpi label="NAV" value={fmtMoney(kpis.nav, currency)} extra={kpis.nav_as_of ? fmtDate(kpis.nav_as_of) : null} calc={calcOf("value", kpis.nav)} />
            <Kpi label="DPI" value={kpis.dpi == null ? "—" : `${fmtRatio(kpis.dpi)}×`} calc={calcOf("dpi", kpis.dpi)} />
            <Kpi label="TVPI" value={kpis.tvpi == null ? "—" : `${fmtRatio(kpis.tvpi)}×`} calc={calcOf("tvpi", kpis.tvpi)} />
            <Kpi
              label="XIRR"
              value={
                kpis.xirr != null
                  ? `${(Number(kpis.xirr) * 100).toFixed(1)}%`
                  // #34: null XIRR with needs_nav reads "Awaiting NAV", not an error.
                  : kpis.xirr_confidence === "needs_nav" ? "Awaiting NAV" : kpis.xirr_reason || "N/A"
              }
              extra={<ConfidenceChip confidence={kpis.xirr_confidence} />}
              calc={calcOf("xirr", kpis.xirr)}
            />
          </div>
        </div>
      )}

      {kpis?.flags?.length > 0 && (
        <ul className="mb-3 text-[11px] text-amber-800 list-disc pl-5" data-testid="kpi-flags">
          {kpis.flags.map((f) => <li key={f}>{f}</li>)}
        </ul>
      )}

      {points && (
        <div className="inline-flex items-center rounded border border-gray-200 bg-white p-0.5 mb-2" data-testid="position-view-toggle">
          {["ledger", "timeline"].map((v) => (
            <button key={v} type="button" onClick={() => setView(v)} aria-pressed={view === v}
              className={`px-2.5 py-1 rounded text-xs font-medium ${view === v ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-50"}`}>
              {v === "timeline" ? "Timeline" : "Ledger"}
            </button>
          ))}
        </div>
      )}
      {v4.enabled && leafId && <V4AddRow leafId={leafId} positionKey={positionKey} />}
      {v4.enabled && points && view === "ledger" && <V4PointsList points={points} leafId={leafId} positionKey={positionKey} />}
      {points && view === "timeline" ? (
        <div className="border border-gray-200 rounded-md bg-white p-3">
          <PositionTimeline points={points} currency={currency} />
        </div>
      ) : (<>
      {/* Value-over-time sparkline */}
      <ValueSparkline documents={docs || []} currency={currency} />

      <div className="border border-gray-200 rounded-md overflow-hidden bg-white mt-3">
        {editable && (
          <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
            <button
              type="button"
              onClick={() => setShowAddForm((v) => !v)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 bg-white text-xs text-gray-700 hover:bg-gray-100"
            >
              <Plus className="w-3 h-3" /> Add entry
            </button>
          </div>
        )}

        {/* Add entry form — only shown when streamId is available (POC bridge path) and the backend allows edits */}
        {editable && showAddForm && (
          <form
            onSubmit={handleAddLine}
            className="flex flex-wrap items-end gap-2 px-3 py-2 border-b border-blue-100 bg-blue-50/30"
          >
            <div className="flex flex-col gap-0.5">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider">Date</label>
              <input
                type="date"
                value={addDate}
                onChange={(e) => setAddDate(e.target.value)}
                required
                disabled={addBusy}
                className="h-7 px-2 rounded border border-gray-300 bg-white text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider">Type</label>
              <select
                value={addTypeSel}
                onChange={(e) => setAddTypeSel(e.target.value)}
                disabled={addBusy}
                className="h-7 px-2 rounded border border-gray-300 bg-white text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
              >
                {POC_ADD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            {!isClose && (
              <>
                <div className="flex flex-col gap-0.5">
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider">Amount</label>
                  <input
                    type="number"
                    value={addAmount}
                    onChange={(e) => setAddAmount(e.target.value)}
                    placeholder="Amount"
                    step="any"
                    required
                    disabled={addBusy}
                    className="h-7 px-2 w-32 rounded border border-gray-300 bg-white text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider">CCY</label>
                  <select
                    value={addCcy}
                    onChange={(e) => setAddCcy(e.target.value)}
                    disabled={addBusy}
                    className="h-7 px-2 rounded border border-gray-300 bg-white text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
                  >
                    {["USD", "EUR", "GBP", "ILS", "CHF", "JPY", "CAD", "AUD"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <div className="flex flex-col gap-0.5 w-full sm:w-56">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider">Note (optional)</label>
              <Textarea
                value={addNote}
                onChange={(e) => setAddNote(e.target.value.slice(0, NOTE_MAX_LEN))}
                maxLength={NOTE_MAX_LEN}
                disabled={addBusy}
                placeholder="Context for this entry…"
                className="text-xs min-h-[28px] h-7 py-1"
              />
            </div>
            <button
              type="submit"
              disabled={addBusy || !addDate || (!isClose && !addAmount)}
              className="h-7 px-3 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors self-end"
            >
              {addBusy ? "…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddForm(false); setAddDate(""); setAddAmount(""); setAddNote(""); }}
              disabled={addBusy}
              className="h-7 px-2 rounded text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 self-end"
            >
              Cancel
            </button>
          </form>
        )}

        <DocumentTimelineTable
          documents={docsNewestFirst}
          currency={currency}
          selectedDocId={selectedDoc?.blob_name || null}
          onSelectDoc={(docId, doc) => {
            if (docId && doc) {
              trackEvent("document_view", {
                portal: doc.portal || "",
                fund_name: fundName,
              });
              setSelectedDoc({
                blob_name: docBlobName(doc),
                original_filename: doc.original_filename,
                download_url: doc.download_url,
              });
            } else {
              setSelectedDoc(null);
            }
          }}
          onMutationDone={refetchAll}
          buildCorrectPath={() => ""}
          buildRejectPath={(inputId) =>
            `/api/ledger/poc-inspection/input/${inputId}/discard`
          }
          onCorrect={async (contribution) => {
            // Extracted rows are view-only; client-input edits not yet surfaced here.
            if (!contribution?.source || contribution.source === "extracted") {
              toast.info("Extracted fields are view-only — correct the source document.");
            } else {
              toast.info("Edit client-input rows via the Ledger Inspection page.");
            }
          }}
          onReject={handleReject}
        />
      </div>
      <DocumentDetailDialog
        doc={selectedDoc}
        open={!!selectedDoc}
        onOpenChange={(o) => { if (!o) setSelectedDoc(null); }}
        onEditField={async () => { toast.info("Documents are read-only in the ledger view."); }}
      />
      </>)}
    </div>
  );
}

// ── Shared cell renderer — used by both MemberRow (rollup) and PositionSubRow ────
// Returns one <td> per column for a data object (member or position dict).
// editCtx (optional): { editable, streamId, token, onApplied } — when given and col.id is one of
// the backend-accepted override fields (OVERRIDABLE_FIELDS), the cell renders as an
// EditableMetricCell (inline field_override affordance, #57 BUILD item 2) instead of a plain
// <td>. Omitted/absent streamId (e.g. a multi-position rollup row with no single resolvable
// stream) falls back to the read-only <td> — never offers an edit that would 400 server-side.
function renderDataCells(data, visibleColumns, currency, editCtx, calc = null) {
  // calc = {sources, version}: v4 leaf/position — every calculated cell gets its explanation hover
  const hv = (col, shown, node) => (calc && COL_METRIC[col.id]
    ? <CalcCell metric={COL_METRIC[col.id]} sources={calc.sources} shown={shown} currency={currency} version={calc.version}>{node}</CalcCell>
    : node);
  return visibleColumns.map((col) => {
    const pred = SHOW[col.show] || (() => true);
    const satisfies = pred(data);

    if (!satisfies) {
      return (
        <td key={col.id} className="py-3 px-3 text-xs text-right font-mono text-gray-400">—</td>
      );
    }

    if (col.kind === "nav_inv") {
      const invested = data.capital_contributed_cumulative ?? data.capital_called_cumulative;
      return (
        <td key={col.id} className="py-3 px-3 text-xs text-right font-mono text-gray-700">
          {hv(col, data.nav == null || invested == null || Number(invested) <= 0 ? null : Number(data.nav) / Number(invested), fmtNavInv(data.nav, invested))}
        </td>
      );
    }

    if (col.kind === "pct") {
      const display = fmtIrr(data[col.id], data.irr_reliable);
      const est = data.irr_kind && data.irr_kind !== "exact";
      return display != null
        ? <td key={col.id} className="py-3 px-3 text-xs text-right font-mono text-gray-700" title={est ? data.irr_kind : undefined}>
            {hv(col, data.irr_reliable ? data.irr : null, <>{display}</>)}{est && <span className="ml-1 rounded bg-indigo-50 px-1 text-[10px] text-indigo-700">est.</span>}
          </td>
        : <td key={col.id} className="py-3 px-3 text-xs text-right font-mono text-gray-400" title={data.irr_reason || "no dated cashflow timeline"}>
            {data.irr_reason || "N/A"}
          </td>;
    }

    if (col.kind === "money") {
      const raw = col.id === "capital_contributed_cumulative"
        ? (data.capital_contributed_cumulative ?? data.capital_called_cumulative)
        : data[col.id];
      const displayVal = col.id === "uncalled"
        ? (hasRealUnfunded(data) ? fmtMoney(data.uncalled, currency) : "—")
        : fmtMoney(raw, currency);
      const asOfStr = col.as_of && data[col.as_of] ? fmtDate(data[col.as_of]) : null;
      const formattedNode = (
        <>
          {displayVal}
          {asOfStr && <span className="ml-1 text-[10px] text-gray-400">{asOfStr}</span>}
        </>
      );

      if (editCtx?.editable && editCtx.streamId && OVERRIDABLE_FIELDS.has(col.id)) {
        const extractedKey = `${col.id}_extracted`;
        return (
          <EditableMetricCell
            key={col.id}
            field={col.id}
            streamId={editCtx.streamId}
            token={editCtx.token}
            editable={editCtx.editable}
            rawValue={raw}
            formattedValue={formattedNode}
            extractedRawValue={data[extractedKey]}
            overridden={data[extractedKey] != null}
            onApplied={editCtx.onApplied}
          />
        );
      }

      return (
        <td key={col.id} className="py-3 px-3 text-xs text-right font-mono text-gray-700 whitespace-nowrap">
          {hv(col, raw, formattedNode)}
        </td>
      );
    }

    if (col.kind === "ratio") {
      const isTvpi = col.id === "tvpi";
      return (
        <td key={col.id} className={`py-3 px-3 text-xs text-right font-mono ${isTvpi ? "font-semibold text-gray-900" : "text-gray-700"}`}>
          {hv(col, data[col.id], fmtRatio(data[col.id]))}
        </td>
      );
    }

    const raw = data[col.id];
    return <td key={col.id} className="py-3 px-3 text-xs text-right font-mono text-gray-700">{raw == null ? "—" : String(raw)}</td>;
  });
}

// ── Position sub-row — one expandable row per position under a multi-position member ──
function PositionSubRow({ position, fundName, investorName, currency, visibleColumns, editable, onDiscard, leafId = null }) {
  const [open, setOpen] = useState(false);
  const v4 = useV4Edit();
  const calc = v4.isV4 && leafId
    ? { version: v4.version, sources: [{ leafId, fundName, investorName, positionKey: position.position_key ?? "" }] }
    : null;
  const token = localStorage.getItem("token");
  const editCtx = { editable, streamId: position.stream_id || null, token, onApplied: onDiscard };
  return (
    <>
      <tr
        className="hover:bg-blue-50/40 border-b border-gray-100 last:border-b-0 cursor-pointer bg-gray-50/30"
        onClick={() => setOpen((v) => !v)}
      >
        {/* indent spacer (checkbox col) */}
        <td className="py-2 pl-4 pr-2 w-8" />
        {/* position label + expand toggle */}
        <td className="py-2 pr-4 text-xs font-medium text-gray-700">
          <div className="flex items-center gap-1.5 min-w-0 pl-5">
            {open
              ? <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
              : <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />}
            <span className="truncate text-gray-600">{position.position_label || position.position_key || "Position"}</span>
          </div>
        </td>
        {/* shared data cells — field_override editable when this position resolves to a real stream */}
        {renderDataCells(position, visibleColumns, currency, editCtx, calc)}
        {/* #57 BUILD item 2: per-position discard (POST /overlay kind=discard, stream_id-scoped) */}
        <td className="py-2 pr-3 pl-1 w-8" onClick={(e) => e.stopPropagation()}>
          <DiscardControl
            streamId={position.stream_id || null}
            editable={editable}
            token={token}
            onDiscarded={onDiscard}
            label={position.position_label || investorName}
          />
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={visibleColumns.length + 3} className="p-0 bg-blue-50/20">
            <MemberTimeline
              fundName={fundName}
              investorName={investorName}
              currency={currency}
              positionKey={position.position_key}
              streamId={position.stream_id || null}
              leafId={position.leaf_id || null}
              editable={editable}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Member-level residual row — distributions not attributable to any position ────
// Shown only when member.has_member_level_activity is true (e.g. BDO דניאל ₪516,637).
function MemberResidualRow({ fundName, investorName, currency, streamId, visibleColumns, editable, leafId = null }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        className="hover:bg-amber-50/40 border-b border-gray-100 last:border-b-0 cursor-pointer opacity-70"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="py-2 pl-4 pr-2 w-8" />
        <td className="py-2 pr-4 text-xs text-gray-500 italic">
          <div className="flex items-center gap-1.5 min-w-0 pl-5">
            {open
              ? <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
              : <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />}
            <span className="truncate">Member-level activity (not attributed to a position)</span>
          </div>
        </td>
        {/* empty cells for data columns */}
        {visibleColumns.map((col) => (
          <td key={col.id} className="py-2 px-3 text-xs text-right font-mono text-gray-300">—</td>
        ))}
        <td className="py-2 pr-3 pl-1 w-8" />
      </tr>
      {open && (
        <tr>
          <td colSpan={visibleColumns.length + 3} className="p-0 bg-amber-50/10">
            <MemberTimeline
              fundName={fundName}
              investorName={investorName}
              currency={currency}
              positionKey=""
              streamId={streamId}
              leafId={leafId}
              editable={editable}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Member row — full-width table row layout (Change 1) ──────────────────────────
// Uses a <tr> inside the FundRow <table> for proper column alignment.
// Named export for tests.
export function MemberRow({
  member, fundName, currency, onDiscard, visibleColumns, inCompareSet, onCompareToggle, editable = false,
  linkModeActive = false, linkSelected = false, onLinkToggle, onUnlinkMember,
}) {
  const [open, setOpen] = useState(false);
  const v4 = useV4Edit();
  const [discarding, setDiscarding] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const compareKey = `${member.portal}|${member.fund_name}|${member.investor_name}`;
  const token = localStorage.getItem("token");

  // Position model: a member holds N positions (99% have 1, BDO דניאל has 2+)
  const positions = member.positions && member.positions.length ? member.positions : [member];
  const isMulti = positions.length > 1;
  // Single-position members resolve to one real stream_id — safe to offer field_override there.
  // Multi-position rollups render via renderDataCells too, but a synthetic rollup stream_id can
  // fail the backend's stream-existence guard, so no editCtx is passed in that case (below).
  const calc = v4.isV4 && member.leaf_id
    ? { version: v4.version, sources: [{ leafId: member.leaf_id, fundName, investorName: member.investor_name }] }
    : null;
  const singleEditCtx = !isMulti
    ? { editable, streamId: member.stream_id || null, token, onApplied: onDiscard }
    : undefined;

  const handleDiscard = async (e) => {
    e.stopPropagation();
    if (!editable) {
      toast.info("Read-only in the ledger view.");
      return;
    }
    setDiscarding(true);
    try {
      const token = localStorage.getItem("token");
      const res = await ledgerFetch("/api/ledger/positions/discard", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          portal: member.portal,
          fund_name: member.fund_name,
          investor_name: member.investor_name,
          discarded: true,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`"${member.investor_name}" hidden — find it in Show discarded`);
      if (onDiscard) onDiscard();
    } catch (err) {
      toast.error("Could not discard position: " + (err?.message || "error"));
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <>
      <tr
        className="hover:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer"
        data-tour="ledger-member"
        onClick={() => setOpen((v) => !v)}
      >
        {/* Compare checkbox — replaced by the "select to link" checkbox while link mode targets this fund */}
        <td className="py-3 pl-4 pr-2 w-8" onClick={(e) => e.stopPropagation()}>
          {linkModeActive ? (
            <Checkbox
              checked={linkSelected === true}
              onCheckedChange={() => onLinkToggle && onLinkToggle(member)}
              aria-label={`Select ${member.investor_name} to link`}
              data-testid={`link-checkbox-${compareKey}`}
              className="w-3.5 h-3.5"
            />
          ) : (
            <Checkbox
              checked={inCompareSet === true}
              onCheckedChange={() => onCompareToggle && onCompareToggle(compareKey)}
              aria-label={`Add ${member.investor_name} to compare`}
              data-testid={`compare-checkbox-${compareKey}`}
              className="w-3.5 h-3.5"
            />
          )}
        </td>
        {/* Expand toggle + investor name */}
        <td className="py-3 pr-4 text-xs font-medium text-gray-900">
          <div className="flex items-center gap-1.5 min-w-0">
            {open
              ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
            <span className="truncate">{member.investor_name}</span>
            {isMulti && (
              <Badge variant="secondary" className="ml-1 text-[10px] flex-shrink-0">
                {positions.length} positions
              </Badge>
            )}
            {member.is_exited && (
              <Badge variant="outline" className="ml-1 text-[10px] border-amber-300 text-amber-700 bg-amber-50 flex-shrink-0">
                exited
              </Badge>
            )}
            {member.flags?.length > 0 && (
              <Badge variant="outline" className="ml-1 text-[10px] border-amber-300 text-amber-700 flex-shrink-0"
                title={member.flags.join(" · ")} data-testid="flags-badge">
                {member.flags.length} flag{member.flags.length > 1 ? "s" : ""}
              </Badge>
            )}
            {/* #57 BUILD item 3b: linked-member badge + Unlink (non-destructive: reverts the
                link_member overlay row(s), never touches the underlying streams) */}
            {member.linked === true && (
              <Badge
                variant="outline"
                className="ml-1 text-[10px] border-indigo-300 text-indigo-700 bg-indigo-50 flex-shrink-0"
                data-testid="linked-badge"
              >
                linked
              </Badge>
            )}
            {member.linked === true && editable && (
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  setUnlinking(true);
                  try {
                    await onUnlinkMember(member);
                  } finally {
                    setUnlinking(false);
                  }
                }}
                disabled={unlinking}
                className="text-[10px] text-indigo-600 hover:text-indigo-800 underline disabled:opacity-50 flex-shrink-0"
                data-testid="unlink-member-btn"
              >
                {unlinking ? "…" : "Unlink"}
              </button>
            )}
          </div>
        </td>

        {/* Kit-driven data columns — render rollup (member top-level) for all cases.
            editCtx is only passed for single-position members (a resolvable native stream_id) —
            multi-position rollups edit at the PositionSubRow level instead. */}
        {renderDataCells(member, visibleColumns, currency, singleEditCtx, calc)}

        {/* Discard button — mutation affordance, hidden when the backend won't accept edits */}
        <td className="py-3 pr-3 pl-1 w-8" onClick={(e) => e.stopPropagation()}>
          {v4.enabled && <V4RemoveLeaf leafId={member.leaf_id || null} />}
          {editable && (
            <button
              type="button"
              onClick={handleDiscard}
              disabled={discarding}
              className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 disabled:opacity-50 transition-colors"
              title="Discard this position (hide from view)"
              aria-label="Discard position"
              data-testid="discard-position-btn"
            >
              <EyeOff className="w-3.5 h-3.5" />
            </button>
          )}
        </td>
      </tr>

      {/* Expanded area — spans all columns */}
      {open && !isMulti && (
        <tr>
          <td colSpan={visibleColumns.length + 3} className="p-0 bg-gray-50/60">
            <MemberTimeline
              fundName={fundName}
              investorName={member.investor_name}
              currency={currency}
              streamId={member.stream_id || null}
              leafId={member.leaf_id || null}
              editable={editable}
            />
          </td>
        </tr>
      )}

      {/* Multi-position: nested position sub-rows */}
      {open && isMulti && (
        <tr>
          <td colSpan={visibleColumns.length + 3} className="p-0 bg-gray-50/40">
            <table className="w-full border-collapse">
              <tbody>
                {positions.map((pos) => (
                  <PositionSubRow
                    key={pos.position_key || pos.stream_id}
                    position={pos}
                    fundName={fundName}
                    investorName={member.investor_name}
                    currency={currency}
                    visibleColumns={visibleColumns}
                    editable={editable}
                    onDiscard={onDiscard}
                    leafId={member.leaf_id || null}
                  />
                ))}
                {member.has_member_level_activity && (
                  <MemberResidualRow
                    fundName={fundName}
                    investorName={member.investor_name}
                    currency={currency}
                    streamId={member.stream_id || null}
                    leafId={member.leaf_id || null}
                    visibleColumns={visibleColumns}
                    editable={editable}
                  />
                )}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Fund row (Card; Change 1 — full-width table layout) ──────────────────────────
function FundRow({
  fund, portalId, onDiscard, compareSet, onCompareToggle, editable,
  mergeModeActive = false, mergeSelected = false, onMergeToggle, onUnmergeFund,
  linkMode = false, linkGroup, onLinkToggle, onUnlinkMember,
}) {
  const [open, setOpen] = useState(false);
  const [unmerging, setUnmerging] = useState(false);
  const members = fund.members || [];
  const fundCcy = safeCurrencyCode(fund.currency);
  const mixedCurrencies = members.some((m) => safeCurrencyCode(m.currency) !== fundCcy);
  const fundKey = `${portalId}|${fund.fund_name}`;
  const hasMerged = members.some((m) => m.merged === true);
  // Link mode is "active" for this fund's members when no other fund currently owns the
  // in-progress selection (linkGroup.fundKey) — the same-fund constraint (Gate-C parity).
  const linkActiveForThisFund = linkMode && (!linkGroup?.fundKey || linkGroup.fundKey === fundKey);

  // Kit-driven visible columns for this fund.
  const visibleColumns = getVisibleColumns(fund, members);

  // Kit summary chips: show a chip per "money" always-column, plus Commitment when any member has real unfunded.
  const kit = fund.kit || DEFAULT_KIT;
  const kitCols = kit.columns || DEFAULT_KIT.columns;

  // "always" money columns drive the summary chips (Invested / Distributed / Current Value equivalents).
  const alwaysMoneyColumns = kitCols.filter((c) => c.kind === "money" && c.show === "always");

  // Commitment chip: shown only when fund has real unfunded pledges (same gate as today).
  const showCommitChip = fundHasUnfunded(members);
  const commitCol = kitCols.find((c) => c.id === "commitment_total");

  // Aggregate values for summary chips.
  const totalInvested = members.reduce((s, m) => s + (m.capital_contributed_cumulative ?? m.capital_called_cumulative ?? 0), 0);
  const fundTotals = {
    capital_contributed_cumulative: totalInvested,
    distributions_cumulative: fund.total_distributed,
    nav: fund.total_nav,
  };

  function chipRaw(colId) {
    return colId in fundTotals ? fundTotals[colId] : members.reduce((s, m) => s + (Number(m[colId]) || 0), 0);
  }
  function chipValue(colId) {
    return fmtMoney(chipRaw(colId), fund.currency);
  }

  // v4: each fund figure is the pool of its members' rows (fetched on first hover)
  const v4 = useV4Edit();
  const calcSources = v4.isV4 && members.length && members.every((m) => m.leaf_id)
    ? members.map((m) => ({ leafId: m.leaf_id, fundName: fund.fund_name, investorName: m.investor_name, label: m.investor_name }))
    : null;
  const calcWrap = (metric, shown, node) => (calcSources && metric
    ? <CalcCell metric={metric} sources={calcSources} shown={shown} currency={fund.currency} version={v4.version} align="left">{node}</CalcCell>
    : node);

  // Fund-level NAV/Inv: Σnav / Σinvested
  const fundNavInv = fmtNavInv(fund.total_nav, totalInvested);

  return (
    <Card className="mb-3 max-w-full">
      <CardContent className="p-0">
        {/* Fund card header */}
        <div className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 min-w-0">
          {/* #57 BUILD item 3a: merge-mode fund checkbox — a sibling of the open-toggle button
              (not nested inside it) so selecting doesn't also expand/collapse the card. */}
          {mergeModeActive && editable && (
            <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
              <Checkbox
                checked={mergeSelected === true}
                onCheckedChange={() => onMergeToggle && onMergeToggle(portalId, fund)}
                aria-label={`Select ${fund.fund_display_name || fund.fund_name} to merge`}
                data-testid={`merge-fund-checkbox-${fundKey}`}
                className="w-3.5 h-3.5"
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex-1 flex items-center gap-3 text-left min-w-0"
            data-tour="ledger-fund-toggle"
          >
            {open ? <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-gray-900 truncate uppercase">{prettyFund(fund.fund_display_name || fund.fund_name)}</h3>
                {portalId && (
                  <Badge variant="outline" className="text-[10px] text-gray-400 border-gray-200 flex-shrink-0">
                    {portalLabel(portalId)}
                  </Badge>
                )}
                <Badge variant="secondary" className="text-[10px] flex-shrink-0">
                  {members.length} {members.length === 1 ? "investor" : "investors"}
                </Badge>
                {mixedCurrencies ? (
                  <Badge className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 flex-shrink-0" title="mixed currencies">{fundCcy}</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] text-gray-500 flex-shrink-0">{fundCcy}</Badge>
                )}
                {/* #57 BUILD item 3a: merged-fund badge (non-destructive; Unmerge reverts the
                    merge_fund overlay rows, never the underlying streams) */}
                {hasMerged && (
                  <Badge
                    variant="outline"
                    className="text-[10px] border-indigo-300 text-indigo-700 bg-indigo-50 flex-shrink-0"
                    data-testid="merged-badge"
                  >
                    merged
                  </Badge>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-0.5 text-xs text-gray-500">
                {showCommitChip && commitCol && (
                  <span>{commitCol.label} <span className="font-mono text-gray-900">{fmtMoney(fund.total_commitment, fund.currency)}</span></span>
                )}
                {alwaysMoneyColumns.map((col) => (
                  <span key={col.id}>{col.label} <span className="font-mono text-gray-900">{calcWrap(COL_METRIC[col.id], chipRaw(col.id), chipValue(col.id))}</span></span>
                ))}
                {totalInvested > 0 && (
                  <span>Value/Inv <span className="font-mono text-gray-900">{calcWrap("rvpi", fund.total_nav == null ? null : Number(fund.total_nav) / totalInvested, fundNavInv)}</span></span>
                )}
              </div>
            </div>
          </button>
          {members[0]?.leaf_id && (
            <V4RenameFund leafId={members[0].leaf_id} fundName={fund.fund_display_name || fund.fund_name} />
          )}
          {hasMerged && editable && (
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                setUnmerging(true);
                try {
                  await onUnmergeFund(fund);
                } finally {
                  setUnmerging(false);
                }
              }}
              disabled={unmerging}
              className="flex-shrink-0 text-[10px] text-indigo-600 hover:text-indigo-800 underline disabled:opacity-50"
              data-testid="unmerge-fund-btn"
            >
              {unmerging ? "…" : "Unmerge"}
            </button>
          )}
        </div>

        {/* Expanded: full-width table (Change 1) */}
        {open && (
          <div className="border-t border-gray-100">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {/* checkbox col */}
                  <th className="w-8 py-2 pl-4 pr-2" />
                  {/* investor name — left aligned, takes remaining space */}
                  <th className="py-2 pr-4 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                    Investor
                  </th>
                  {/* numeric columns — right aligned, fixed width */}
                  {visibleColumns.map((col) => (
                    <th
                      key={col.id}
                      className="py-2 px-3 text-right text-[10px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap"
                    >
                      {colHeaderLabel(col)}
                    </th>
                  ))}
                  {/* discard btn col */}
                  <th className="w-8 py-2 pr-3 pl-1" />
                </tr>
              </thead>
              <tbody>
                {members.map((m, i) => {
                  const cKey = `${m.portal}|${m.fund_name}|${m.investor_name}`;
                  return (
                    <MemberRow
                      key={i}
                      member={m}
                      fundName={fund.fund_name}
                      currency={fund.currency}
                      onDiscard={onDiscard}
                      visibleColumns={visibleColumns}
                      inCompareSet={compareSet ? compareSet.has(cKey) : false}
                      onCompareToggle={onCompareToggle}
                      editable={editable}
                      linkModeActive={linkActiveForThisFund}
                      linkSelected={!!linkGroup?.members?.includes(m)}
                      onLinkToggle={(member) => onLinkToggle && onLinkToggle(fundKey, member)}
                      onUnlinkMember={onUnlinkMember}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Discarded positions panel ─────────────────────────────────────────────────────
// #57 BUILD item 2: prefers the overview's own `discarded` list (render-not-hide, populated by
// overview_from_poc for any POC-mirrored tenant — the same response the rest of the page already
// fetched, no extra round-trip) when the caller supplies it. Falls back to the legacy
// GET /positions/discarded + POST /positions/discard(discarded:false) flow — UNCHANGED — for any
// tenant whose overview response doesn't carry a top-level `discarded` array (non-POC path).
function DiscardedPanel({ onRestored, editable = false, discardedFromOverview }) {
  const usingOverlayList = Array.isArray(discardedFromOverview);
  const [items, setItems] = useState(usingOverlayList ? discardedFromOverview : null);
  const [loading, setLoading] = useState(!usingOverlayList);
  const [restoringKey, setRestoringKey] = useState(null);

  const loadLegacy = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem("token");
    ledgerFetch("/api/ledger/positions/discarded", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => { setItems(j.discarded || []); setLoading(false); })
      .catch(() => { setItems([]); setLoading(false); });
  }, []);

  useEffect(() => {
    if (usingOverlayList) {
      setItems(discardedFromOverview);
      setLoading(false);
      return;
    }
    loadLegacy();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usingOverlayList, discardedFromOverview, loadLegacy]);

  const keyFor = (item) => item.stream_id || `${item.portal}|${item.fund_name}|${item.investor_name}`;

  const handleRestore = async (item) => {
    if (!editable) {
      toast.info("Read-only in the ledger view.");
      return;
    }
    const key = keyFor(item);
    setRestoringKey(key);
    try {
      const token = localStorage.getItem("token");
      if (usingOverlayList) {
        const overlay = await findActiveOverlay(item.stream_id, "discard", token);
        if (!overlay) throw new Error("No active discard found to revert");
        await revertOverlay(overlay.id, token);
      } else {
        const res = await ledgerFetch("/api/ledger/positions/discard", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ portal: item.portal, fund_name: item.fund_name, investor_name: item.investor_name, discarded: false }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
      toast.success(`"${item.investor_name}" restored`);
      if (onRestored) onRestored();
      if (!usingOverlayList) loadLegacy();
    } catch (err) {
      toast.error("Could not restore: " + (err?.message || "error"));
    } finally {
      setRestoringKey(null);
    }
  };

  if (loading) return <Skeleton className="h-16 w-full rounded" />;

  if (!items || items.length === 0) {
    return <p className="text-xs text-gray-400 py-2">No discarded positions.</p>;
  }

  return (
    <div className="divide-y divide-gray-100 border border-gray-200 rounded-md bg-white overflow-hidden">
      {items.map((item) => {
        const key = keyFor(item);
        const restoring = restoringKey === key;
        const fundLabel = prettyFund(item.fund_display_name || item.fund_name);
        return (
          <div key={key} className="flex items-center justify-between px-3 py-2 text-xs gap-3">
            <div className="min-w-0">
              <span className="font-medium text-gray-900 truncate block">{item.investor_name}</span>
              <span className="text-gray-400 truncate block"><span className="uppercase">{fundLabel}</span> · {item.portal}</span>
            </div>
            {editable && (
              <button
                type="button"
                onClick={() => handleRestore(item)}
                disabled={restoring}
                className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 text-xs"
                aria-label={`Restore ${item.investor_name}`}
                data-testid="restore-position-btn"
              >
                <RotateCcw className="w-3 h-3" />
                Restore
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────────
export default function LedgerDashboardPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [serverLocked, setServerLocked] = useState(false);
  const [showDiscarded, setShowDiscarded] = useState(false);
  const [discardedPanelKey, setDiscardedPanelKey] = useState(0);

  // GenerateReportDialog's client groups (non-demo only) — fetched once via
  // the authenticated api client, never ledgerFetch, and never for the demo
  // tenant (zero-network invariant).
  const [groups, setGroups] = useState([]);

  // ── Feature: Lines tab (#63) — flat cross-position ledger feed, fetched lazily the first
  // time the tab is opened (mirrors MemberTimeline's fetch-on-expand pattern; never fetched
  // while the Positions tab is active).
  const [view, setView] = useState("positions"); // "positions" | "lines"
  const [linesData, setLinesData] = useState(null);
  const [linesLoading, setLinesLoading] = useState(false);
  const [linesError, setLinesError] = useState(false);

  // Lines tab search/filter/sort — pure client-side over the already-fetched
  // flat feed (applyLinesFilters/sortLines in lib/ledgerFilters.js).
  const EMPTY_LINES_FILTERS = { search: "", portal: "", fund: "", investor: "", group: "", type: "" };
  const [linesFilters, setLinesFilters] = useState(EMPTY_LINES_FILTERS);
  const [linesSort, setLinesSort] = useState("date_desc");
  // #63 follow-up: group-by-member view toggle + the doc popup's selected line
  // (mirrors MemberTimeline's selectedDoc -> DocumentDetailDialog pattern, page-level here).
  const [linesGroupBy, setLinesGroupBy] = useState(false);
  const [selectedLineDoc, setSelectedLineDoc] = useState(null);
  // Export flow (founder request, verbatim: "i want a flow: export xlsx/csv then
  // filters") — ONE Export button opens ExportLinesDialog; the dialog owns format +
  // filter selection + download, this page only mounts/unmounts it.
  const [showExportLinesDialog, setShowExportLinesDialog] = useState(false);

  // Change 2: Sort by dropdown — replaces the Portfolio/Leaderboard toggle
  const [fundSort, setFundSort] = useState("currentValue");

  // ── Feature: filters ─────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    portal: "",
    fund: "",
    investor: "",
    currency: "",
    assetClass: "",
    docTypes: [],
    dateFrom: "",
    dateTo: "",
    xirrMin: "",
    xirrMax: "",
    showExited: true,
    search: "",
  });

  // ── Feature: compare ─────────────────────────────────────────────────────────
  const [compareSet, setCompareSet] = useState(new Set());
  const [compareOpen, setCompareOpen] = useState(false);

  const handleCompareToggle = useCallback((key) => {
    setCompareSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleCompareRemove = useCallback((key) => {
    setCompareSet((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  // ── #57 BUILD item 3: Merge funds (fund-variant merge, client-facing with undo) ──────────────
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState([]); // [{ portalId, fund }]
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  const handleMergeToggle = useCallback((portalId, fund) => {
    setMergeSelection((prev) => {
      const already = prev.some((e) => e.portalId === portalId && e.fund.fund_name === fund.fund_name);
      if (already) return prev.filter((e) => !(e.portalId === portalId && e.fund.fund_name === fund.fund_name));
      if (prev.length > 0 && prev[0].portalId !== portalId) {
        toast.info("Select funds within the same portal to merge.");
        return prev;
      }
      return [...prev, { portalId, fund }];
    });
  }, []);

  const handleMergeModeToggle = useCallback(() => {
    setLinkMode(false);
    setLinkGroup({ fundKey: null, members: [] });
    setMergeMode((v) => {
      const next = !v;
      if (!next) setMergeSelection([]);
      return next;
    });
  }, []);

  // ── #57 BUILD item 3: Link members (within-fund identity link, client-facing with undo) ──────
  const [linkMode, setLinkMode] = useState(false);
  const [linkGroup, setLinkGroup] = useState({ fundKey: null, members: [] });
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const handleLinkToggle = useCallback((fundKey, member) => {
    setLinkGroup((prev) => {
      const already = prev.members.includes(member);
      if (already) {
        const nextMembers = prev.members.filter((m) => m !== member);
        return { fundKey: nextMembers.length ? prev.fundKey : null, members: nextMembers };
      }
      if (prev.fundKey && prev.fundKey !== fundKey) {
        toast.info("Select members within the same fund to link.");
        return prev;
      }
      return { fundKey, members: [...prev.members, member] };
    });
  }, []);

  const handleLinkModeToggle = useCallback(() => {
    setMergeMode(false);
    setMergeSelection([]);
    setLinkMode((v) => {
      const next = !v;
      if (!next) setLinkGroup({ fundKey: null, members: [] });
      return next;
    });
  }, []);

  const demo = isLedgerDemoUser(user);
  // Plan gate: a non-demo user whose plan is known and not Full never fires a ledger request.
  // A user object without a plan field falls through to the backend, which answers 403 ledger_locked.
  const isAllowed = !(user && !demo && user.plan && user.plan !== "full");

  // Fetch client groups once for the report dialog — non-demo entitled tenants
  // only. MUST NOT fire for the demo tenant (zero-network invariant) and uses
  // the authenticated api client (axios), not ledgerFetch.
  useEffect(() => {
    if (demo || !isAllowed) return;
    let cancelled = false;
    api
      .get("/groups")
      .then((res) => { if (!cancelled) setGroups(res.data?.groups || []); })
      .catch(() => { if (!cancelled) setGroups([]); });
    return () => { cancelled = true; };
  }, [demo, isAllowed]);

  // v4 manual edits: bumped after every save/undo so overview, lines, timelines and the Edits panel refetch.
  const [editVersion, setEditVersion] = useState(0);

  const fetchOverview = useCallback((silent = false) => {
    if (!isAllowed) return;
    let cancelled = false;
    const token = localStorage.getItem("token");
    if (silent !== true) setLoading(true);
    ledgerFetch("/api/ledger/overview", { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 503) { setUnavailable(true); setLoading(false); return; }
        if (res.status === 403) {
          const body = await res.json().catch(() => null);
          if (body?.detail === "ledger_locked") { setServerLocked(true); setLoading(false); return; }
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json()); setLoading(false);
      })
      .catch((err) => { if (!cancelled) { toast.error("Failed to load ledger: " + (err?.message || "error")); setLoading(false); } });
    return () => { cancelled = true; };
  }, [isAllowed]);

  useEffect(() => {
    setLedgerDemoMode(demo); // must be set before fetchOverview so ledgerFetch routes correctly
    const cancel = fetchOverview();
    return cancel;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed]);

  // Lines tab: fetch GET /api/ledger/lines only when the tab is opened — never on mount, never
  // while the Positions tab is active (keeps the demo-tenant "zero network calls" invariant intact).
  useEffect(() => {
    if (view !== "lines" || !isAllowed) return;
    let cancelled = false;
    const token = localStorage.getItem("token");
    if (editVersion === 0) setLinesLoading(true); // after an edit, refresh in place (no skeleton flash)
    setLinesError(false);
    ledgerFetch("/api/ledger/lines", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => { if (!cancelled) setLinesData(j); })
      .catch(() => { if (!cancelled) setLinesError(true); })
      .finally(() => { if (!cancelled) setLinesLoading(false); });
    return () => { cancelled = true; };
  }, [view, isAllowed, editVersion]);

  const changed = useCallback(() => {
    fetchOverview(true);
    setEditVersion((v) => v + 1);
  }, [fetchOverview]);

  const handlePositionDiscarded = useCallback(() => {
    fetchOverview();
    setDiscardedPanelKey((k) => k + 1);
  }, [fetchOverview]);

  const handlePositionRestored = useCallback(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handleUnmergeFund = useCallback(async (fund) => {
    const token = localStorage.getItem("token");
    const streamIds = collectMergedStreamIds(fund);
    if (streamIds.length === 0) return;
    try {
      await revertOverlaysForStreams(streamIds, "merge_fund", token);
      toast.success("Unmerged");
      fetchOverview();
    } catch (err) {
      toast.error("Unmerge failed: " + (err?.message || "error"));
    }
  }, [fetchOverview]);

  const handleUnlinkMember = useCallback(async (member) => {
    const token = localStorage.getItem("token");
    const streamIds = collectLinkedStreamIds(member);
    if (streamIds.length === 0) return;
    try {
      await revertOverlaysForStreams(streamIds, "link_member", token);
      toast.success("Unlinked");
      fetchOverview();
    } catch (err) {
      toast.error("Unlink failed: " + (err?.message || "error"));
    }
  }, [fetchOverview]);

  const portals = useMemo(() => data?.portals || [], [data]);

  // Derive filtered portals (client-side; only non-discarded members visible)
  const filteredPortals = useMemo(() => applyLedgerFilters(portals, filters), [portals, filters]);

  // Lines tab: flat feed straight off the fetch, before fuzzy canonicalization.
  const rawLines = useMemo(() => linesData?.lines || [], [linesData]);

  // #63 follow-up: display-layer maps (lib/linesDisplay.js, frozen contract) — real display
  // names, Title Case, group-by-member, faceted dropdown labels, and the CSV export all key
  // off these. foldTight-keyed, a DIFFERENT key space from linesFundDisplayNames below
  // (fund-slug-keyed, feeds search) — the two are not merged on purpose.
  const fundMap = useMemo(() => buildFundDisplayMap(portals), [portals]);
  const memberMap = useMemo(() => buildMemberDisplayMap(rawLines, groups), [rawLines, groups]);
  const resolveGroup = useMemo(() => buildGroupResolver(groups), [groups]);

  // Fuzzy-match canonicalization (client-feedback round, supersedes fold-equality-only
  // dropdown consolidation) — canonicalize fund/investor identities ONCE here, over the raw
  // lines feed AND the commitment snapshot rows (same identity space), so every downstream
  // consumer (dropdowns/filters/grouping/CSV — buildLinesFacets, applyLinesFilters, LinesTable,
  // groupLinesByMember, buildLinesCsv) reads already-de-duplicated values with zero changes to
  // their own fold-equality logic.
  const canonFund = useMemo(() => {
    const fundValues = [...rawLines.map((l) => l.fund), ...commitmentRows(portals).map((c) => c.fund)];
    return buildFuzzyCanon(fundValues, fundMap);
  }, [rawLines, portals, fundMap]);
  const canonInvestor = useMemo(() => {
    const investorValues = [...rawLines.map((l) => l.investor), ...commitmentRows(portals).map((c) => c.investor)];
    return buildFuzzyCanon(investorValues, memberMap);
  }, [rawLines, portals, memberMap]);

  // Lines tab: flat feed the filters/facets/CSV all derive from — fund/investor identities
  // fuzzy-canonicalized; falsy values pass through canonFund/canonInvestor unchanged.
  const allLines = useMemo(
    () => rawLines.map((l) => ({ ...l, fund: canonFund(l.fund), investor: canonInvestor(l.investor) })),
    [rawLines, canonFund, canonInvestor]
  );

  // Lines tab: cascading (faceted) dropdown options — each axis's option list derives from
  // the lines surviving every OTHER active filter (buildLinesFacets, linesDisplay.js).
  // fund/investor values are CANONICAL foldTight keys (consolidated dropdown entries, one
  // per fold-identity, Title-Case labels); portals are raw values; groupIds facet too, and
  // the axis's own current selection is always kept in its list.
  const linesOptions = useMemo(
    () => buildLinesFacets(allLines, linesFilters, { groups, fundMap, memberMap }),
    [allLines, linesFilters, groups, fundMap, memberMap]
  );

  // Slug -> display title map from the overview: lets search match the fund
  // name the client SEES, not just the slug the lines carry.
  const linesFundDisplayNames = useMemo(() => {
    const map = {};
    for (const p of portals) {
      for (const f of p.funds || []) {
        if (f.fund_name && f.fund_display_name) map[f.fund_name] = f.fund_display_name;
      }
    }
    return map;
  }, [portals]);
  const visibleLines = useMemo(
    () => sortLines(applyLinesFilters(allLines, linesFilters, groups, linesFundDisplayNames), linesSort),
    [allLines, linesFilters, groups, linesFundDisplayNames, linesSort]
  );
  const linesFiltersActive = Object.values(linesFilters).some(Boolean);

  // Change 2+3: sorted fund entries (flat list of { fund, portalId })
  const sortedFundEntries = useMemo(() => sortFunds(filteredPortals, fundSort), [filteredPortals, fundSort]);

  // ── Position-book cascading tree (PLAN-position-book-ledger-tree) ────────────────
  // Active only when the bridge marks the payload data.source === "position_book" — the
  // classic flat-fund render (sortedFundEntries above) stays byte-identical for every
  // other payload. applyLedgerFilters runs FIRST (filteredPortals, already computed
  // above); buildBookTree only regroups whatever survived filtering — a member the
  // filters removed can never reappear via the cascade.
  const isBookMode = data?.source === "position_book";
  const bookTree = useMemo(() => buildBookTree(filteredPortals), [filteredPortals]);
  // Auto-expand every PortalCard/MemberCard while a targeted fund/investor/search filter
  // is active — applyLedgerFilters already trimmed the tree to matches only, so once a
  // node survives filtering it IS a match; this just seeds the initial open state (each
  // card's own useState), the user can still collapse it afterwards.
  const cascadeAutoExpand = Boolean(
    filters.fund ||
    (filters.investor && filters.investor.trim() !== "") ||
    (filters.search && filters.search.trim() !== "")
  );

  // Derive fund names for the filter fund single-select
  const allFundNames = useMemo(
    () => [...new Set(portals.flatMap((p) => (p.funds || []).map((f) => f.fund_name)))].sort(),
    [portals]
  );

  // Build portal list for the portal filter dropdown
  const portalOptions = useMemo(
    () => portals.map((p) => ({
      portal: p.portal,
      displayName: portalLabel(p.portal),
      funds: p.funds || [],
    })),
    [portals]
  );

  // Flat member rows for GenerateReportDialog's Client select + fund
  // scoping (non-demo tenants). Walks the same portals→funds→members tree
  // the rest of the page renders from — never `data.investors` (hardcoded
  // `[]` in prod).
  const reportPositions = useMemo(
    () =>
      portals.flatMap((p) =>
        (p.funds || []).flatMap((f) =>
          (f.members || []).map((m) => ({
            portal: p.portal,
            fund_name: m.fund_name,
            fund_display_name: m.fund_display_name || f.fund_display_name || m.fund_name,
            investor_name: m.investor_name,
          }))
        )
      ),
    [portals]
  );

  // Build compare member objects from the current compareSet + portals tree
  const compareMembers = useMemo(() => {
    const result = [];
    for (const portal of portals) {
      for (const fund of portal.funds || []) {
        for (const member of fund.members || []) {
          const key = `${member.portal}|${member.fund_name}|${member.investor_name}`;
          if (compareSet.has(key)) {
            result.push({ ...member, currency: member.currency || fund.currency });
          }
        }
      }
    }
    return result;
  }, [portals, compareSet]);

  // Deep-link: ?portal=<id> — run once after portals load.
  useEffect(() => {
    if (portals.length === 0) return;
    const param = searchParams.get("portal");
    if (!param) return;
    const match = portals.find((p) => p.portal.toLowerCase() === param.toLowerCase());
    if (match) setFilters((prev) => ({ ...prev, portal: match.portal }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portals.length, searchParams]);

  if (!isAllowed || serverLocked) return <LedgerLockedState />;
  if (data?.state === "preparing") return <LedgerPreparingState />;

  // Backend capability flags — fail-closed. Absent/undefined/false ⇒ read-only.
  // v4 books are edited on the CSV rows (V4Edits), not through the generation-two overlay/POC controls.
  const isV4 = data?.source === "v4";
  const v4Editable = isV4 && data?.editable === true;
  const editable = data?.editable === true && !isV4;
  const v4Ctx = { enabled: v4Editable, isV4, version: editVersion, changed };
  const dataAsOf = data?.data_as_of
    ? new Date(data.data_as_of).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : null;

  return (
    <V4EditContext.Provider value={v4Ctx}>
    <div className="w-full min-w-0 space-y-4">
      {/* ── Header row ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 min-w-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">Portfolio Dashboard</h1>
            <Badge variant="outline" className="border-indigo-300 bg-indigo-50 text-indigo-700 text-[10px] font-semibold">v3</Badge>
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px] font-semibold">Beta</Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">Fund × investor positions — invested capital, distributions, and current value — click an investor to see its source documents.</p>
          {dataAsOf && <p className="text-xs text-gray-400 mt-0.5">Data as of {dataAsOf}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {demo ? (
            <GenerateReportDialog
              funds={getDemoReportFundNames()}
              onGenerate={(config) => {
                trackEvent("report_generated", {});
                return buildHtmlReport(getDemoReportPortfolio(), config);
              }}
            />
          ) : (
            <GenerateReportDialog positions={reportPositions} groups={groups} />
          )}
          {/* ── View toggle: Positions / Lines (#63) — same toggle-button idiom as the toolbar
              buttons below, not an ARIA tablist (this page deliberately has none — see the
              "no portal tab list" invariant test). */}
          <div className="inline-flex items-center rounded border border-gray-200 bg-white p-0.5 flex-shrink-0" data-testid="ledger-view-toggle" data-tour="ledger-view-toggle">
            <button
              type="button"
              onClick={() => setView("positions")}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                view === "positions" ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-50"
              }`}
              data-testid="ledger-view-positions-btn"
              aria-pressed={view === "positions"}
            >
              Positions
            </button>
            <button
              type="button"
              onClick={() => setView("lines")}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                view === "lines" ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-50"
              }`}
              data-testid="ledger-view-lines-btn"
              aria-pressed={view === "lines"}
            >
              Lines
            </button>
          </div>
          {view === "positions" && compareSet.size >= 2 && (
            <button
              type="button"
              onClick={() => { trackEvent("compare_used", { count: compareSet.size }); setCompareOpen(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-medium transition-colors"
              data-testid="compare-open-btn"
            >
              <GitCompare className="w-3.5 h-3.5" />
              Compare ({compareSet.size})
            </button>
          )}
          {view === "positions" && (
            <button
              type="button"
              onClick={() => setShowDiscarded((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-medium transition-colors ${
                showDiscarded
                  ? "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
              data-testid="show-discarded-toggle"
            >
              <EyeOff className="w-3.5 h-3.5" />
              {showDiscarded ? "Hide discarded" : "Show discarded"}
            </button>
          )}
          {/* #57 BUILD item 3: merge/link mode toggles — client-facing, editable-gated */}
          {view === "positions" && editable && (
            <button
              type="button"
              onClick={handleMergeModeToggle}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-medium transition-colors ${
                mergeMode
                  ? "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
              data-testid="merge-funds-toggle"
            >
              <GitMerge className="w-3.5 h-3.5" />
              {mergeMode ? "Cancel merge" : "Merge funds"}
            </button>
          )}
          {view === "positions" && editable && (
            <button
              type="button"
              onClick={handleLinkModeToggle}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-medium transition-colors ${
                linkMode
                  ? "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
              data-testid="link-members-toggle"
            >
              <Link2 className="w-3.5 h-3.5" />
              {linkMode ? "Cancel link" : "Link members"}
            </button>
          )}
        </div>
      </div>

      {/* ── Merge/Link selection bars ───────────────────────────────────────── */}
      {view === "positions" && mergeMode && mergeSelection.length >= 2 && (
        <div className="flex items-center gap-2 rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
          <span>{mergeSelection.length} funds selected</span>
          <button
            type="button"
            onClick={() => setMergeDialogOpen(true)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
            data-testid="open-merge-dialog"
          >
            Merge funds
          </button>
          <button type="button" onClick={() => setMergeSelection([])} className="text-indigo-600 hover:text-indigo-800 underline">
            Clear
          </button>
        </div>
      )}
      {view === "positions" && linkMode && linkGroup.members.length >= 2 && (
        <div className="flex items-center gap-2 rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
          <span>{linkGroup.members.length} members selected</span>
          <button
            type="button"
            onClick={() => setLinkDialogOpen(true)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
            data-testid="open-link-dialog"
          >
            Link as same member
          </button>
          <button
            type="button"
            onClick={() => setLinkGroup({ fundKey: null, members: [] })}
            className="text-indigo-600 hover:text-indigo-800 underline"
          >
            Clear
          </button>
        </div>
      )}

      {/* ── Discarded panel ───────────────────────────────────────────────── */}
      {view === "positions" && showDiscarded && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wider">Discarded positions</p>
          <DiscardedPanel
            key={discardedPanelKey}
            onRestored={handlePositionRestored}
            editable={editable}
            discardedFromOverview={data?.discarded}
          />
        </div>
      )}

      {/* ── Loading skeletons ─────────────────────────────────────────────── */}
      {view === "positions" && loading && (
        <div className="space-y-2">
          <Skeleton className="h-9 w-64 rounded" />
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
        </div>
      )}

      {/* ── Unavailable / empty ───────────────────────────────────────────── */}
      {view === "positions" && (unavailable || (!loading && portals.length === 0)) && (
        <Card className="border-gray-200">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <FileText className="w-12 h-12 text-gray-300" />
            <p className="text-base font-medium text-gray-700">Ledger not yet available for this environment</p>
          </CardContent>
        </Card>
      )}

      {/* ── Lines tab (#63) — flat cross-position ledger feed ───────────────── */}
      {view === "lines" && (
        <div className="w-full min-w-0 space-y-4">
          {linesLoading && (
            <div className="space-y-2">
              <Skeleton className="h-9 w-64 rounded" />
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-20 w-full rounded-lg" />
            </div>
          )}
          {!linesLoading && linesError && (
            <Card className="border-gray-200">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-3">
                <FileText className="w-12 h-12 text-gray-300" />
                <p className="text-base font-medium text-gray-700">Could not load ledger lines</p>
              </CardContent>
            </Card>
          )}
          {!linesLoading && !linesError && linesData && (
            <>
              {/* No per-tab "Data as of" stamp — the page header already shows
                  it on both tabs; a second identical stamp reads as a bug. */}

              {/* ── Search / filter / sort bar ─────────────────────────────── */}
              <div className="flex flex-wrap items-center gap-2" data-testid="lines-filter-bar">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    value={linesFilters.search}
                    onChange={(e) => setLinesFilters((p) => ({ ...p, search: e.target.value }))}
                    placeholder="Search portal, fund, member or document..."
                    className="h-8 w-full rounded-md border border-gray-200 bg-white pl-8 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300"
                    data-testid="lines-filter-search"
                  />
                </div>

                <Select
                  value={linesFilters.portal || "__all__"}
                  onValueChange={(v) => setLinesFilters((p) => ({ ...p, portal: v === "__all__" ? "" : v }))}
                >
                  <SelectTrigger className="h-8 text-xs w-[130px]" data-testid="lines-filter-portal">
                    <SelectValue placeholder="All portals" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__" className="text-xs">All portals</SelectItem>
                    {linesOptions.portals.map((p) => (
                      <SelectItem key={p.value} value={p.value} className="text-xs">{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={linesFilters.fund || "__all__"}
                  onValueChange={(v) => setLinesFilters((p) => ({ ...p, fund: v === "__all__" ? "" : v }))}
                >
                  <SelectTrigger className="h-8 text-xs w-[160px]" data-testid="lines-filter-fund">
                    <SelectValue placeholder="All funds" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__" className="text-xs">All funds</SelectItem>
                    {linesOptions.funds.map((f) => (
                      <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={linesFilters.investor || "__all__"}
                  onValueChange={(v) => setLinesFilters((p) => ({ ...p, investor: v === "__all__" ? "" : v }))}
                >
                  <SelectTrigger className="h-8 text-xs w-[150px]" data-testid="lines-filter-member">
                    <SelectValue placeholder="All members" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__" className="text-xs">All members</SelectItem>
                    {linesOptions.investors.map((inv) => (
                      <SelectItem key={inv.value} value={inv.value} className="text-xs">{inv.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {groups.length > 0 && (
                  <Select
                    value={linesFilters.group || "__all__"}
                    onValueChange={(v) => setLinesFilters((p) => ({ ...p, group: v === "__all__" ? "" : v }))}
                  >
                    <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="lines-filter-group">
                      <SelectValue placeholder="All groups" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__" className="text-xs">All groups</SelectItem>
                      {linesOptions.groupIds.map((gid) => {
                        const g = groups.find((x) => x.group_id === gid);
                        return (
                          <SelectItem key={gid} value={gid} className="text-xs">{g ? g.name : gid}</SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}

                <div className="flex items-center rounded-md border border-gray-200 bg-white p-0.5">
                  {[
                    ["", "All", "lines-type-all"],
                    ["capital_call_event_amount", "Calls", "lines-type-calls"],
                    ["distribution_event_amount", "Distributions", "lines-type-dists"],
                  ].map(([val, label, tid]) => (
                    <button
                      key={tid}
                      type="button"
                      onClick={() => setLinesFilters((p) => ({ ...p, type: val }))}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        linesFilters.type === val ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-50"
                      }`}
                      aria-pressed={linesFilters.type === val}
                      data-testid={tid}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Sort</span>
                  <div className="flex items-center rounded-md border border-gray-200 bg-white p-0.5">
                    {[
                      ["date_desc", "Newest", "lines-sort-date-desc"],
                      ["date_asc", "Oldest", "lines-sort-date-asc"],
                      ["type", "By type", "lines-sort-type"],
                    ].map(([val, label, tid]) => (
                      <button
                        key={tid}
                        type="button"
                        onClick={() => setLinesSort(val)}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                          linesSort === val ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-50"
                        }`}
                        aria-pressed={linesSort === val}
                        data-testid={tid}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* By-member grouping toggle — same toggle-button idiom as the Sort buttons above */}
                <div className="flex items-center rounded-md border border-gray-200 bg-white p-0.5">
                  <button
                    type="button"
                    onClick={() => setLinesGroupBy((v) => !v)}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      linesGroupBy ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-50"
                    }`}
                    aria-pressed={linesGroupBy}
                    data-testid="lines-groupby-member-btn"
                  >
                    By member
                  </button>
                </div>

                {!demo && <button
                  type="button"
                  onClick={() => setShowExportLinesDialog(true)}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                  data-testid="lines-export-csv-btn"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export
                </button>}

                {linesFiltersActive && (
                  <button
                    type="button"
                    onClick={() => setLinesFilters(EMPTY_LINES_FILTERS)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100"
                    data-testid="lines-filter-clear"
                  >
                    <X className="w-3 h-3" />
                    Clear
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3">
                <p className="text-xs text-gray-500" data-testid="lines-count">
                  {visibleLines.length} of {allLines.length} lines
                </p>
                {data?.source === "v4" && !demo && (
                  ["navs", "cashflows", "diff"].map((w) => (
                    <button key={w} type="button" onClick={() => saveV4Csv({ which: w }, `${w}.csv`)} data-testid={`v4-${w}-csv`}
                      className="px-2.5 py-1 rounded border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50">
                      {w}.csv
                    </button>
                  ))
                )}
              </div>

              <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
                {(() => {
                  const openDoc = (line) => setSelectedLineDoc({
                    blob_name: docBlobName(line),
                    original_filename: line.original_filename,
                    download_url: line.download_url,
                  });
                  return v4Editable ? (
                    <V4LinesTable lines={visibleLines} fundMap={fundMap} memberMap={memberMap} onSelectDoc={openDoc} />
                  ) : (
                    <LinesTable
                      lines={visibleLines}
                      groupByMember={linesGroupBy}
                      fundMap={fundMap}
                      memberMap={memberMap}
                      resolveGroup={resolveGroup}
                      onSelectDoc={openDoc}
                    />
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {view === "positions" && !loading && !unavailable && portals.length > 0 && (
        <div className="w-full min-w-0 space-y-4">
          {/* ── Filter bar ────────────────────────────────────────────────── */}
          <div data-tour="ledger-filters">
            <LedgerFilterBar
              filters={filters}
              onFiltersChange={setFilters}
              filterOptions={data?.filter_options}
              fundNames={allFundNames}
              portals={portalOptions}
            />
          </div>

          {/* ── Sort by + fund count row (Change 2) ───────────────────────── */}
          <div className="flex items-center gap-3 flex-wrap" data-testid="fund-sort-row">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Sort by</span>
              <Select value={fundSort} onValueChange={setFundSort}>
                <SelectTrigger className="h-8 text-xs w-[160px]" data-testid="fund-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FUND_SORT_METRICS.map((m) => (
                    <SelectItem key={m.value} value={m.value} className="text-xs">
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-gray-400">
              {sortedFundEntries.length} fund{sortedFundEntries.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* ── Continuous vertical fund list (classic) OR the position-book cascading
              PORTAL → MEMBER → FUND tree (isBookMode) ─────────────────────────── */}
          <div className="w-full min-w-0 space-y-0">
            {isBookMode ? (
              bookTree.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                  <FileText className="w-12 h-12 text-gray-200" />
                  <p className="text-sm text-gray-500">No funds match the current filters.</p>
                </div>
              ) : (
                bookTree.map((portalNode) => (
                  <PortalCard
                    key={portalNode.portal}
                    portalNode={portalNode}
                    autoExpand={cascadeAutoExpand}
                    fundSort={fundSort}
                    FundRowComponent={FundRow}
                    fundRowProps={{
                      editable,
                      compareSet,
                      onCompareToggle: handleCompareToggle,
                      onDiscard: handlePositionDiscarded,
                      mergeModeActive: mergeMode,
                      mergeSelection,
                      onMergeToggle: handleMergeToggle,
                      onUnmergeFund: handleUnmergeFund,
                      linkMode,
                      linkGroup,
                      onLinkToggle: handleLinkToggle,
                      onUnlinkMember: handleUnlinkMember,
                    }}
                  />
                ))
              )
            ) : sortedFundEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                <FileText className="w-12 h-12 text-gray-200" />
                <p className="text-sm text-gray-500">No funds match the current filters.</p>
              </div>
            ) : (
              sortedFundEntries.map(({ fund, portalId }, i) => (
                <FundRow
                  key={`${portalId}|${fund.fund_name}|${i}`}
                  fund={fund}
                  portalId={portalId}
                  onDiscard={handlePositionDiscarded}
                  compareSet={compareSet}
                  onCompareToggle={handleCompareToggle}
                  editable={editable}
                  mergeModeActive={mergeMode}
                  mergeSelected={mergeSelection.some((e) => e.portalId === portalId && e.fund.fund_name === fund.fund_name)}
                  onMergeToggle={handleMergeToggle}
                  onUnmergeFund={handleUnmergeFund}
                  linkMode={linkMode}
                  linkGroup={linkGroup}
                  onLinkToggle={handleLinkToggle}
                  onUnlinkMember={handleUnlinkMember}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* ── Compare slide-over ────────────────────────────────────────────── */}
      <CompareSheet
        open={compareOpen}
        onOpenChange={setCompareOpen}
        compareMembers={compareMembers}
        onRemove={handleCompareRemove}
      />

      {/* ── Merge funds / Link members dialogs (#57 BUILD item 3) ──────────── */}
      <MergeFundsDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        funds={mergeSelection}
        token={localStorage.getItem("token")}
        onMerged={() => {
          setMergeSelection([]);
          setMergeMode(false);
          fetchOverview();
        }}
      />
      <LinkMemberDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        members={linkGroup.members}
        token={localStorage.getItem("token")}
        onLinked={() => {
          setLinkGroup({ fundKey: null, members: [] });
          setLinkMode(false);
          fetchOverview();
        }}
      />

      {/* ── Lines tab doc popup (#63 follow-up) — the SAME DocumentDetailDialog component
          MemberTimeline uses above (PDF via the same-origin proxy, never the SAS link),
          page-level here since the Lines tab has no per-row expand state to nest it in. */}
      <DocumentDetailDialog
        doc={selectedLineDoc}
        open={!!selectedLineDoc}
        onOpenChange={(o) => { if (!o) setSelectedLineDoc(null); }}
        onEditField={async () => { toast.info("Documents are read-only in the ledger view."); }}
      />

      {/* ── Export flow (founder request: "export xlsx/csv then filters") — replaces the
          old direct "Export CSV" button, which just dumped whatever the page's OWN filters
          currently showed. Mounted only while open (never a hidden Dialog) so it always
          starts fresh on `allLines`/`linesFilters` as they stand at click-time. */}
      {showExportLinesDialog && (
        <ExportLinesDialog
          allLines={allLines}
          portals={portals}
          groups={groups}
          fundMap={fundMap}
          memberMap={memberMap}
          resolveGroup={resolveGroup}
          canonFund={canonFund}
          canonInvestor={canonInvestor}
          initialFilters={linesFilters}
          sort={linesSort}
          portalOptions={linesOptions.portals}
          fundOptions={linesOptions.funds}
          investorOptions={linesOptions.investors}
          onClose={() => setShowExportLinesDialog(false)}
        />
      )}
    </div>
    </V4EditContext.Provider>
  );
}
